/**
 * Copyright (c) 2020
 *
 * Contains the representation of a CustomerSubscription
 *
 * @summary customerSubscription class definition
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-06-08 10:21:56
 * Last modified  : 2021-08-17 16:19:24
 */

import Subscription from '@/models/customersManager/entity/Subscription.js'
import Customer from '@/models/customersManager/entity/Customer.js'
import toBoolean from 'to-boolean'

class CustomerSubscription {

    /**
     * @type id Integer
     */
    #id

    /**
     * @type owner Boolean
     */
    #owner

    /**
     * @type customer null|Customer
     */
    #customer = null

    /**
     * @type subscription Subscription
     */
    #subscription = null

    /**
     * Not owned by customers-manager data-model but provided by customers-manager
     *
     * @type ownerCustomer null|Customer
     */
    #ownerCustomer = null

    /**
     * Not owned by customers-manager data-model and not provided by customers-manager
     *
     * @type active Boolean
     */
    #active = false

    /**
     * @param rawCustomerSubscription null|Object
     */
    constructor(rawCustomerSubscription = null) {
        if (rawCustomerSubscription !== null) {
            const { id, owner, customer, subscription, ownerCustomer, active } = rawCustomerSubscription
            this.id = id
            this.owner = owner
            this.customer = customer
            this.subscription = subscription
            this.ownerCustomer = ownerCustomer
            this.active = active
        }
    }

    /**
     * @returns Integer
     */
    get id () {
        return this.#id
    }

    /**
     * @param id String
     * @returns void
     */
    set id (id) {
        this.#id = parseInt(id)
    }

    /**
     * @returns Boolean
     */
    get owner () {
        return this.#owner
    }

    /**
     * @param owner String
     * @returns void
     */
    set owner (owner) {
        this.#owner = toBoolean(owner)
    }

    /**
     * @returns null|Customer
     */
    get customer () {
        return this.#customer
    }

    /**
     * @param rawCustomer null|Object
     * @returns void
     */
    set customer (rawCustomer) {
        this.#customer = null
        if (rawCustomer !== null && typeof rawCustomer !== 'undefined') {
            this.#customer = new Customer(rawCustomer)
        }
    }

    /**
     * @returns Subscription
     */
    get subscription () {
        return this.#subscription
    }

    /**
     * @param rawSubscription Object
     * @returns void
     */
    set subscription (rawSubscription) {
        this.#subscription = null
        if (rawSubscription !== null && typeof rawSubscription !== 'undefined') {
            this.#subscription = new Subscription(rawSubscription)
        }
    }

    /**
     * @returns null|Customer
     */
    get ownerCustomer () {
        return this.#ownerCustomer
    }

    /**
     * @param rawOwnerCustomer null|Object
     * @returns void
     */
    set ownerCustomer (rawOwnerCustomer) {
        this.#ownerCustomer = null
        if (rawOwnerCustomer !== null && typeof rawOwnerCustomer !== 'undefined') {
            this.#ownerCustomer = new Customer(rawOwnerCustomer)
        }
    }

    /**
     * @returns Boolean
     */
    get active () {
        return this.#active
    }

    /**
     * @param active String
     * @returns void
     */
    set active (active) {
        this.#active = false
        if (active !== null && typeof active !== 'undefined') {
            this.#active = toBoolean(active)
        }
    }
}

export default CustomerSubscription
